import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Loader2,
  Search,
  UserPlus,
  Upload,
  Key,
  UserX,
  CheckCircle,
  AlertCircle,
  Users,
} from 'lucide-react';
import { getUsers, createUser, bulkCreateUsers, disableUser, resetUserPassword } from '../../services/api';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Add user dialog
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ firstName: '', lastName: '', email: '' });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserSuccess, setAddUserSuccess] = useState(null);
  const [addUserError, setAddUserError] = useState(null);

  // Bulk add dialog
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [bulkData, setBulkData] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkErrors, setBulkErrors] = useState([]);

  // Reset password dialog
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Disable user
  const [disableLoading, setDisableLoading] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      (user.firstName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.lastName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserLoading(true);
    setAddUserError(null);
    setAddUserSuccess(null);

    try {
      const response = await createUser(newUser);
      setAddUserSuccess(response.data);
      setNewUser({ firstName: '', lastName: '', email: '' });
      fetchUsers();
    } catch (error) {
      setAddUserError('Failed to create user. Please try again.');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleBulkAdd = async () => {
    setBulkLoading(true);
    setBulkResult(null);
    setBulkErrors([]);

    const lines = bulkData.trim().split('\n');
    const parsedUsers = [];
    const errors = [];

    lines.forEach((line, index) => {
      const parts = line.split(',').map((p) => p.trim());
      if (parts.length !== 3) {
        errors.push(`Line ${index + 1}: Invalid format (expected: first_name,last_name,email)`);
        return;
      }
      const [firstName, lastName, email] = parts;
      if (!firstName || !lastName || !email) {
        errors.push(`Line ${index + 1}: Missing required field`);
        return;
      }
      if (!email.includes('@')) {
        errors.push(`Line ${index + 1}: Invalid email format`);
        return;
      }
      parsedUsers.push({ firstName, lastName, email });
    });

    if (errors.length > 0) {
      setBulkErrors(errors);
      setBulkLoading(false);
      return;
    }

    try {
      const response = await bulkCreateUsers(parsedUsers);
      setBulkResult(response.data);
      setBulkData('');
      fetchUsers();
    } catch (error) {
      setBulkErrors(['Failed to create users. Please try again.']);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!tempPassword.trim()) return;
    setResetLoading(true);

    try {
      await resetUserPassword(selectedUser.id, tempPassword);
      setResetSuccess(true);
    } catch (error) {
      console.error('Failed to reset password:', error);
    } finally {
      setResetLoading(false);
    }
  };

  const handleDisableUser = async (userId) => {
    setDisableLoading(userId);
    try {
      await disableUser(userId);
      fetchUsers();
    } catch (error) {
      console.error('Failed to disable user:', error);
    } finally {
      setDisableLoading(null);
    }
  };

  const openResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setTempPassword('');
    setResetSuccess(false);
    setResetPasswordOpen(true);
  };

  if (loading) {
    return (
      <div className="p-6 md:p-12 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12" data-testid="admin-users-page">
      <div className="max-w-5xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600 mt-1">Add, search, and manage member accounts.</p>
          </div>
          <div className="flex gap-2">
            {/* Add Single User */}
            <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
              <DialogTrigger asChild>
                <Button className="bg-amber-600 hover:bg-amber-700" data-testid="add-user-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-user-dialog">
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                  <DialogDescription>
                    Create a new member account. They will receive a temporary password.
                  </DialogDescription>
                </DialogHeader>
                {addUserSuccess ? (
                  <div className="py-4 text-center">
                    <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                    <p className="font-medium text-gray-900">User created successfully!</p>
                    <p className="text-sm text-gray-600 mt-2">
                      Temporary password: <code className="bg-gray-100 px-2 py-1 rounded">{addUserSuccess.temporaryPassword}</code>
                    </p>
                    <p className="text-xs text-gray-500 mt-2">User must change password on first login.</p>
                    <Button className="mt-4" onClick={() => { setAddUserOpen(false); setAddUserSuccess(null); }}>
                      Close
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleAddUser}>
                    {addUserError && (
                      <Alert variant="destructive" className="mb-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{addUserError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName">First Name</Label>
                          <Input
                            id="firstName"
                            value={newUser.firstName}
                            onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                            required
                            data-testid="add-user-firstname"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName">Last Name</Label>
                          <Input
                            id="lastName"
                            value={newUser.lastName}
                            onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                            required
                            data-testid="add-user-lastname"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          required
                          data-testid="add-user-email"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={addUserLoading} data-testid="add-user-submit">
                        {addUserLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Create User
                      </Button>
                    </DialogFooter>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {/* Bulk Add Users */}
            <Dialog open={bulkAddOpen} onOpenChange={setBulkAddOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="bulk-add-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Bulk Add
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg" data-testid="bulk-add-dialog">
                <DialogHeader>
                  <DialogTitle>Bulk Add Users</DialogTitle>
                  <DialogDescription>
                    Paste or type user data, one per line. Format: first_name,last_name,email
                  </DialogDescription>
                </DialogHeader>
                {bulkResult ? (
                  <div className="py-4 text-center">
                    <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                    <p className="font-medium text-gray-900">{bulkResult.created} users created successfully!</p>
                    <Button className="mt-4" onClick={() => { setBulkAddOpen(false); setBulkResult(null); }}>
                      Close
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="py-4">
                      {bulkErrors.length > 0 && (
                        <Alert variant="destructive" className="mb-4">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            <ul className="list-disc pl-4">
                              {bulkErrors.map((err, i) => (
                                <li key={i}>{err}</li>
                              ))}
                            </ul>
                          </AlertDescription>
                        </Alert>
                      )}
                      <Textarea
                        value={bulkData}
                        onChange={(e) => setBulkData(e.target.value)}
                        placeholder="John,Smith,john.smith@example.com
Jane,Doe,jane.doe@example.com"
                        rows={8}
                        className="font-mono text-sm"
                        data-testid="bulk-data-textarea"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        No headers required. Each line: first_name,last_name,email
                      </p>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleBulkAdd} disabled={bulkLoading || !bulkData.trim()} data-testid="bulk-submit-btn">
                        {bulkLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Import Users
                      </Button>
                    </DialogFooter>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Search */}
        <Card className="border-gray-100 mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="user-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="border-gray-100" data-testid="users-table-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Dues</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">
                        {user.firstName} {user.lastName}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.status === 'active' ? 'outline' : 'destructive'}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`text-sm ${
                          user.duesStatus === 'Yes' ? 'text-emerald-600' :
                          user.duesStatus === 'No' ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {user.duesStatus}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openResetPasswordDialog(user)}
                            data-testid={`reset-password-btn-${user.id}`}
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDisableUser(user.id)}
                            disabled={disableLoading === user.id || user.status === 'disabled'}
                            data-testid={`disable-user-btn-${user.id}`}
                          >
                            {disableLoading === user.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <UserX className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
          <DialogContent data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Set a temporary password for {selectedUser?.firstName} {selectedUser?.lastName}.
                They will be required to change it on next login.
              </DialogDescription>
            </DialogHeader>
            {resetSuccess ? (
              <div className="py-4 text-center">
                <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                <p className="font-medium text-gray-900">Password reset successfully!</p>
                <p className="text-sm text-gray-600 mt-2">
                  User must change password on first login.
                </p>
                <Button className="mt-4" onClick={() => setResetPasswordOpen(false)}>
                  Close
                </Button>
              </div>
            ) : (
              <>
                <div className="py-4">
                  <Label htmlFor="tempPassword">Temporary Password</Label>
                  <Input
                    id="tempPassword"
                    type="text"
                    value={tempPassword}
                    onChange={(e) => setTempPassword(e.target.value)}
                    placeholder="Enter temporary password"
                    className="mt-2"
                    data-testid="temp-password-input"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    The user will be required to change this password on next login.
                  </p>
                </div>
                <DialogFooter>
                  <Button onClick={handleResetPassword} disabled={resetLoading || !tempPassword.trim()} data-testid="reset-password-submit">
                    {resetLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Reset Password
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminUsersPage;
